import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ModalWrapper from '../ModalWrapper';
import CustomChip from '../chips/CustomChip';
import ButtonComponent from '../customButton';
import TableComponent from '../TableComponent';
import { getSchedule } from '../../utils/api/schedule_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { dateFormat } from '../../utils/date';
import {
  formatRunTime12Hour,
  formatScheduleCadence,
  getScheduleStatusChipColors,
  getScheduleFireOutcomeChipColors,
  getScheduleFireOutcomeLabel,
} from '../../utils/scheduleDisplay';
import moment from 'moment';
import DEFAULT_ROUTES from '../../constants/routes';
import detailStyles from '../../styles/createTestLaunch/scheduleDetailModal.module.scss';

const {
  LaunchType,
  LaunchTypeLabel,
  ScheduleStatus,
} = require('../../enums/ScheduleEnums');

const DEFAULT_PAGINATION = { page: 0, pageSize: 25 };

function ScheduleDetailModal({
  open,
  onClose,
  scheduleId,
  projectId,
  groupId,
  onEdit,
}) {
  const theme = useTheme();
  const palette = theme.palette.customColor;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [schedule, setSchedule] = useState(null);
  const [fireRows, setFireRows] = useState([]);
  const [paginationModel, setPaginationModel] = useState(DEFAULT_PAGINATION);

  useEffect(() => {
    if (!open || !scheduleId) return undefined;
    let cancelled = false;
    setError('');
    setLoading(true);
    setPaginationModel(DEFAULT_PAGINATION);
    getSchedule({ scheduleId })
      .then((res) => {
        if (cancelled) return;
        if (res.status !== STATUS.SUCCESS) {
          setError(res.error || 'Failed to load schedule');
          setSchedule(null);
          setFireRows([]);
          return;
        }
        const data = res.data || {};
        const sched = data.schedule || data;
        setSchedule(sched);
        const history = data.fireHistory || data.fire_history || [];
        setFireRows(
          history.map((row) => ({
            id: row.id,
            scheduled_fire_time: row.scheduled_fire_time,
            outcome: row.outcome,
            detail: row.detail,
            launch_id: row.launch_id,
            launch_name: row.test_launch?.name || row.test_launch?.id || null,
            included_case_count: row.included_case_count,
          }))
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, scheduleId]);

  const pagedFireRows = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    return fireRows.slice(start, start + paginationModel.pageSize);
  }, [fireRows, paginationModel]);

  const statusChipColors = getScheduleStatusChipColors(palette, schedule?.status);
  const launchType = schedule?.launch_type || LaunchType?.COMPLETE || 'COMPLETE';
  const isAutomated = launchType === (LaunchType?.AUTOMATED || 'AUTOMATED');
  const isManual = launchType === (LaunchType?.MANUAL || 'MANUAL');
  const launchTypeChipColors = {
    color: isAutomated ? palette.blue[50] : isManual ? palette.slate[50] : palette.green[50],
    textColor: isAutomated ? palette.blue[600] : isManual ? palette.slate[700] : palette.green[600],
    borderColor: isAutomated ? palette.blue[200] : isManual ? palette.slate[200] : palette.green[200],
  };

  const fireColumns = [
    {
      field: 'scheduled_fire_time',
      headerName: 'Scheduled slot',
      flex: 1.2,
      minWidth: 160,
      renderCell: (params) => (params.value ? dateFormat(params.value) : '—'),
    },
    {
      field: 'outcome',
      headerName: 'Outcome',
      flex: 1.2,
      minWidth: 160,
      renderCell: (params) => {
        if (!params.value) return '—';
        const label = getScheduleFireOutcomeLabel(params.value);
        const colors = getScheduleFireOutcomeChipColors(palette, params.value);
        return (
          <CustomChip
            label={label}
            color={colors.color}
            textColor={colors.textColor}
            borderColor={colors.borderColor}
            size="small"
          />
        );
      },
    },
    {
      field: 'included_case_count',
      headerName: 'Cases',
      flex: 0.5,
      minWidth: 80,
      renderCell: (params) => (
        params.value == null ? <span className={detailStyles.muted}>—</span> : params.value
      ),
    },
    {
      field: 'launch_id',
      headerName: 'Launch',
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        if (!row.launch_id) {
          return <span className={detailStyles.muted}>—</span>;
        }
        const label = row.launch_name || 'View launch';
        if (projectId && groupId) {
          const to = `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${projectId}${DEFAULT_ROUTES.TEST_GROUPS}/${groupId}/launch-details/${row.launch_id}`;
          return (
            <RouterLink
              to={to}
              className={detailStyles.launchLink}
              onClick={(e) => e.stopPropagation()}
            >
              {label}
            </RouterLink>
          );
        }
        return label;
      },
    },
    {
      field: 'detail',
      headerName: 'Detail',
      flex: 1.4,
      minWidth: 160,
      renderCell: (params) => (
        <span className={params.value ? undefined : detailStyles.muted} title={params.value || ''}>
          {params.value || '—'}
        </span>
      ),
    },
  ];

  const isFireEmpty = !loading && fireRows.length === 0;

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      title={schedule?.name || 'Schedule detail'}
      size="large"
      className={detailStyles.modalWider}
      bodyClassName={detailStyles.modalBody}
      footer={(
        <div className={detailStyles.footerBar}>
          {schedule && (
            <ButtonComponent
              ctaText="Edit schedule"
              variant="outline"
              onClick={() => onEdit?.(schedule)}
              disabled={loading || !schedule}
            />
          )}
          <ButtonComponent
            ctaText="Close"
            variant="primary"
            className="white bg-blue600"
            onClick={onClose}
          />
        </div>
      )}
    >
      <div className={detailStyles.root}>
        {error && (
          <Alert severity="error">{error}</Alert>
        )}

        {schedule && (
          <div className={detailStyles.summary}>
            <div className={detailStyles.chipRow}>
              <CustomChip
                label={LaunchTypeLabel[schedule.launch_type] || schedule.launch_type}
                color={launchTypeChipColors.color}
                textColor={launchTypeChipColors.textColor}
                borderColor={launchTypeChipColors.borderColor}
                size="small"
              />
              <CustomChip
                label={String(schedule.status || '').replace(/_/g, ' ')}
                color={statusChipColors.color}
                textColor={statusChipColors.textColor}
                borderColor={statusChipColors.borderColor}
                size="small"
              />
            </div>
            <p className={detailStyles.meta}>
              {formatScheduleCadence(schedule)}
              {' · '}
              {formatRunTime12Hour(schedule.run_time)}
              {' · '}
              Next{' '}
              {schedule.status === ScheduleStatus.ACTIVE && schedule.next_run_at
                ? dateFormat(schedule.next_run_at)
                : '—'}
              {' · '}
              Ends {schedule.end_date ? moment(schedule.end_date).format('MMM D, YYYY') : '—'}
            </p>
          </div>
        )}

        <Box className={detailStyles.tableSection}>
          <TableComponent
            title="Fire history"
            headerActionButtons={[]}
            hideColumnsVisibilityButton
            rows={pagedFireRows}
            columns={fireColumns}
            loading={loading}
            idField="id"
            isCheckbox={false}
            isRowSelection={false}
            isColumnResizable={!loading}
            isPagination
            paginationMode="server"
            rowCount={fireRows.length}
            paginationModel={paginationModel}
            handleCustomPaginationPageChange={(event, page) => (
              setPaginationModel((prev) => ({ ...prev, page }))
            )}
            handleCustomPaginationPageSizeChange={(pageSize) => (
              setPaginationModel({ page: 0, pageSize })
            )}
            isCustomPagination
            filterMode="client"
            sortingMode="client"
            tableHeight="360px"
            getRowHeight={() => 'auto'}
            getEstimatedRowHeight={() => 56}
            customStyles={{
              '& .MuiDataGrid-cell': { py: 1 },
            }}
            isData={!isFireEmpty}
            customNoData={(
              <div className={detailStyles.emptyHistory}>
                <p className={detailStyles.emptyHistoryText}>
                  No fire attempts recorded yet for this schedule.
                </p>
              </div>
            )}
          />
        </Box>
      </div>
    </ModalWrapper>
  );
}

export default ScheduleDetailModal;
